import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import tw from "twin.macro";
import styled from "styled-components";

import { useAuth } from "components/AuthProvider";
import { Card } from "components/cards";
import { H, P, A } from "components/typography";
import Clipboard, { ClipboardButton } from "components/Clipboard";
import { createToast } from "screens/shared/toast";
import { readablePhone } from "helpers/helper";
import { currencyFormatter } from "helpers/formatters";
import dayjs from "dayjs";

import { useMutation } from "react-query";
import { cancelSubscription, syncSubscriptions } from "api/v2/customerSessions";

import Memberships from "./memberships";
import * as Api from "api/api";
import * as Button from "components/typography/Button";

const Wrap = styled.div`
  ${tw`w-full mx-auto px-[15px]`}
  max-width: 1140px;
`;
const Empty = styled.div`
  ${tw`w-full text-center py-[50px]`}

  h5 {
    ${tw`block text-center pt-[20px]`}
    color: #9ea0a5;
  }

  i {
    color: #9ea0a5;
  }
`;

const Profile: React.FC = () => {
  const { loggedIn, user } = useAuth();

  //Allowed users by id for staging/testing purposes only
  // Mike:1, Daniela:4, Allen:8, Kristi:30502, Herbert:48468, Ehsan:10502
  //const allowedIds = [1, 4, 8, 30502, 48468, 10502];
  // Sarah Lickus: 18947, Sujith K.: 37466, Elyse H.: 18853, Theresa C.: 16621, Linda C.: 36781, Carrie S.: 31838, Kathleen B.: 23735, Elizabeth R.: 11193, Leah A.: 16097, Joan T.: 4945
  //const userWhitelist = [
  //  18947, 37466, 18853, 16621, 36781, 31838, 23735, 11193, 16097, 4945
  //];
  //const combinedList = [...allowedIds, ...userWhitelist];

  const syncSubscriptionMutation = useMutation({
    mutationFn: () => syncSubscriptions(),
    onSuccess: async () => {
      window.location.href = "/account";
    }
  });

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);

    if (query.get("success")) {
      syncSubscriptionMutation.mutate();
    }

    if (query.get("canceled")) {
      createToast("Purchase canceled!");
    }
  }, []);

  if (!loggedIn) return null;

  if (!user)
    return (
      <Wrap>
        <Empty>
          <i className="fas fa-user-times fa-5x"></i>
          <h5>That user was not found.</h5>
        </Empty>
      </Wrap>
    );

  const { referral_url, currentUser, token } = user || {};

  const isCurrentUser = `${user.id}` === `${currentUser?.id}`;

  const onCopySuccess = () => createToast("Referral Link Copied");

  const portalSessionUrl = Api.urlFor("/api/v2/users/stripe_portal");

  const cancelSubscriptionMutation = useMutation({
    mutationFn: () => cancelSubscription(),
    onSuccess: async () => {
      window.location.reload();
    }
  });

  const handleSubBtnClick = () => {
    window.location.href = "/subscriptions";
  };

  const handleCancelBtnClick = () => {
    cancelSubscriptionMutation.mutate();
  };

  return (
    <article
      tw="w-full mx-auto flex flex-col gap-6 px-6"
      style={{ maxWidth: 850 }}
    >
      <H.Three
        as="h1"
        tw="py-12 pb-6 border border-0 border-b border-solid border-secondary"
      >
        My Account
      </H.Three>
      <Card.Tertiary tw="gap-4 flex flex-col md:flex-row">
        <div tw="md:w-1/5">
          <P.Medium tw="font-bold">Account Details</P.Medium>
          {isCurrentUser && (
            <Link to="/account/edit" component={A.Primary}>
              Change
            </Link>
          )}
        </div>
        <div tw="grid grid-cols-1 md:grid-cols-2 md:w-4/5 gap-4">
          <div tw="flex flex-row">
            <i className="fas fa-calendar-day" tw="mr-4 text-muted" />
            <P.Medium>{user.name}</P.Medium>
          </div>
          <div tw="flex flex-row">
            <i className="fas fa-phone" tw="mr-4 text-muted" />
            <P.Medium>{readablePhone(user.phone)}</P.Medium>
          </div>
          <div tw="flex flex-row">
            <i className="fas fa-envelope" tw="mr-4 text-muted" />
            <P.Medium>{user.email}</P.Medium>
          </div>
          <div tw="flex flex-row">
            <i className="fas fa-home" tw="mr-4 text-muted" />
            <P.Medium>
              {user && ![user.street, user.city, user.state].includes(null)
                ? `${user.street} ${user.city}, ${user.state} ${user.postal_code}`
                : "Home address is not set"}
            </P.Medium>
          </div>
        </div>
      </Card.Tertiary>

      <Card.Tertiary tw="gap-4 flex flex-col md:flex-row">
        <P.Medium tw="font-bold md:w-1/5 py-2">Subscription</P.Medium>
        <div tw="flex flex-row">
          <i className="fas fa-star" tw="mr-4 text-muted py-2" />
          {user.stripe_subscribed_at == null ? (
            <div tw="flex flex-row gap-4">
              <P.Medium tw="py-2">Regular user</P.Medium>
              <Button.Primary onClick={handleSubBtnClick}>
                Subscribe
              </Button.Primary>
            </div>
          ) : (
            <div tw="flex flex-row gap-4">
              {user.stripe_sub_cancelled ? (
                <div tw="flex flex-col gap-2">
                  <P.Medium tw="py-2">DwellSocial VIP Member</P.Medium>
                  <div tw="flex flex-row gap-1">
                    <P.Medium tw="font-bold py-2">Valid until:</P.Medium>
                    <P.Medium tw="py-2">
                      {dayjs(user.stripe_subscription_end).format("MM/DD/YYYY")}
                    </P.Medium>
                  </div>
                </div>
              ) : (
                <div tw="flex flex-row gap-4">
                  <div tw="flex flex-col gap-2">
                    <div tw="flex flex-row gap-2">
                      <P.Medium tw="py-2">DwellSocial VIP Member</P.Medium>
                      <Button.Tertiary onClick={handleCancelBtnClick}>
                        Cancel Subscription
                      </Button.Tertiary>
                    </div>
                    <div tw="flex flex-row gap-1">
                      <P.Medium tw="font-bold py-2">Renews:</P.Medium>
                      <P.Medium tw="py-2">
                        {dayjs(user.stripe_subscription_end).format(
                          "MM/DD/YYYY"
                        )}
                      </P.Medium>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Card.Tertiary>

      <Card.Tertiary tw="gap-4 flex flex-col md:flex-row">
        <P.Medium tw="font-bold md:w-1/5">Credit Balance</P.Medium>
        <div tw="flex flex-row">
          <i className="fas fa-coins" tw="mr-4 text-muted" />
          <P.Medium>
            {currencyFormatter(Math.abs(parseFloat(user.credit_balance)))}
          </P.Medium>
        </div>
      </Card.Tertiary>
      <Card.Tertiary tw="gap-4 flex flex-col md:flex-row md:items-center">
        <P.Medium tw="font-bold md:w-1/5">Referral Link</P.Medium>
        <div tw="flex flex-col md:flex-row w-full md:w-4/5 gap-2 md:items-center">
          <Clipboard
            id="share-link-copy-url"
            content={referral_url}
            onSuccess={onCopySuccess}
            tw="m-0"
          >
            {referral_url.split("&utm")[0]}
          </Clipboard>
          <ClipboardButton
            content={referral_url}
            onSuccess={onCopySuccess}
            tw="min-w-max px-4 self-stretch"
          >
            <i className="fas fa-link" tw="mr-1"></i>Share via URL
          </ClipboardButton>
        </div>
      </Card.Tertiary>

      <Card.Tertiary tw="gap-4 flex flex-col md:flex-row md:items-center">
        <P.Medium tw="font-bold md:w-1/5">Manage Billing</P.Medium>
        <div tw="flex flex-col md:flex-row w-full md:w-4/5 gap-2 md:items-center">
          <i className="fas fa-credit-card" tw="mr-4 text-muted" />
          <form method="POST" action={portalSessionUrl}>
            <input type="hidden" name="token" value={token} />
            <Button.Tertiary>Update Card</Button.Tertiary>
          </form>
        </div>
      </Card.Tertiary>

      <Memberships />
    </article>
  );
};

export default Profile;
